import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  DollarSign, TrendingUp, Users, Wallet, Plus, Trash2, Edit2, X, Save, AlertCircle,
  FileText, Upload, CheckCircle2, Sparkles, Loader2, ChevronDown, ChevronUp,
  Calendar as CalendarIcon,
} from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";

const STANDARD_SUBS = {
  capacity: [
    "Support Coordination", "Improved Daily Living Skills", "Improved Health & Wellbeing",
    "Improved Relationships", "Improved Living Arrangements",
    "Increased Social & Community Participation", "Finding & Keeping a Job",
    "Improved Learning", "Improved Life Choices",
  ],
  core: [
    "Assistance with Daily Life", "Transport", "Consumables",
    "Assistance with Social & Community Participation",
  ],
  capital: ["Assistive Technology", "Home Modifications"],
};

const ALL_SUBS = [...STANDARD_SUBS.capacity, ...STANDARD_SUBS.core, ...STANDARD_SUBS.capital, "Unknown"];

const CAT_STYLE = {
  core:     { label: "Core Supports",       bg: "#FCEADB", border: "#F4CFA7", text: "#A0521A" },
  capacity: { label: "Capacity Building",   bg: "#DDE7E9", border: "#B8CACD", text: "#284952" },
  capital:  { label: "Capital Supports",    bg: "#FCE7EB", border: "#F4C3CC", text: "#A12545" },
};

const fmt = (n) => Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function ClientBudgetTab({ clientId }) {
  const { user } = useAuth();
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);           // null | 'new' | budget item
  const [uploadingStmt, setUploadingStmt] = useState(false);
  const [preview, setPreview] = useState(null);           // parsed statement waiting to be committed
  const [showAllStatements, setShowAllStatements] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const fileInputRef = useRef(null);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/budget`);
      setBudget(data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load budget.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const seedCategories = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/clients/${clientId}/budget/seed`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't seed categories");
    } finally {
      setSeeding(false);
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Delete this budget line item? Spend already tracked stays on record.")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/budget/items/${itemId}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't delete item");
    }
  };

  const onFilePicked = async (file) => {
    if (!file) return;
    setUploadingStmt(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/clients/${clientId}/budget/statements/parse`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 },
      );
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't parse that statement. Try a clearer file or enter lines manually.");
    } finally {
      setUploadingStmt(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const commitStatement = async (finalPayload) => {
    try {
      await axios.post(`${API}/clients/${clientId}/budget/statements/commit`, finalPayload);
      setPreview(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't save the statement");
    }
  };

  const deleteStatement = async (stmt) => {
    if (!window.confirm(`Remove statement from ${stmt.plan_manager || "plan manager"} (${stmt.entries_count} lines)?\n\nAll spend imported from this statement will be undone.`)) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/budget/statements/${stmt.id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't delete the statement");
    }
  };

  if (loading) return <div className="text-center py-16"><Loader2 className="w-8 h-8 mx-auto animate-spin text-[#E2718A]" /></div>;
  if (error && !budget) return (
    <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-700 flex gap-3 items-start" data-testid="budget-error">
      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /><div>{error}</div>
    </div>
  );
  if (!budget) return null;

  const totalItems = ["core", "capacity", "capital"].reduce((s, c) => s + (budget.grouped[c]?.length || 0), 0);
  const statements = budget.statements || [];
  const visibleStatements = showAllStatements ? statements : statements.slice(0, 3);

  return (
    <div data-testid="client-budget-tab" className="space-y-6">
      {/* Period summary, mirrors the top of a SupportingU statement */}
      <div className="grid sm:grid-cols-3 gap-3">
        <PeriodStat label="Total Period Spend" value={`$${fmt(budget.period_stats.total_spend)}`} icon={DollarSign} hint="Last 30 days" testId="period-spend" />
        <PeriodStat label="Total Invoices"     value={budget.period_stats.total_invoices || 0}   icon={FileText} hint="Last 30 days" testId="period-invoices" />
        <PeriodStat label="Total Providers"    value={budget.period_stats.total_providers || 0}  icon={Users}    hint="Last 30 days" testId="period-providers" />
      </div>

      {/* Plan timeline */}
      <div className="bg-white rounded-3xl border border-[#F0E5DE] p-6" data-testid="plan-timeline">
        <div className="flex items-center gap-2 mb-3">
          <CalendarIcon className="w-4 h-4 text-[#E2718A]" />
          <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80]">Plan Timeline</div>
        </div>
        {budget.plan_start_date && budget.plan_end_date ? (
          <>
            <div className="flex justify-between text-sm mb-2">
              <div>
                <div className="text-xs text-[#6B7A80]">Plan Start</div>
                <div className="font-semibold text-[#1F2A2E]">{fmtDate(budget.plan_start_date)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#6B7A80]">Plan End</div>
                <div className="font-semibold text-[#1F2A2E]">{fmtDate(budget.plan_end_date)}</div>
              </div>
            </div>
            <div className="h-3 w-full rounded-full bg-[#F0E5DE] overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, budget.percent_elapsed || 0)}%`,
                  background: "linear-gradient(90deg, #EA823A 0%, #E2718A 100%)",
                }}
              />
            </div>
            <div className="text-right text-xs font-semibold text-[#6B7A80] mt-2">
              {budget.percent_elapsed != null ? `${budget.percent_elapsed.toFixed(2)}% elapsed` : ""}
            </div>
          </>
        ) : (
          <div className="text-sm text-[#6B7A80] italic">Set the client&apos;s Plan Start &amp; End dates (on the client&apos;s profile) to track timeline.</div>
        )}
      </div>

      {/* Statement upload */}
      <div className="bg-gradient-to-br from-[#F1E4D5] to-white rounded-3xl border border-[#F0E5DE] p-6" data-testid="statement-upload-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-full bg-white border border-[#F0E5DE] flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-[#E2718A]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl text-[#1F2A2E] mb-1">Upload plan manager statement</div>
            <p className="text-sm text-[#6B7A80] leading-relaxed">
              Upload a PDF, CSV, or Excel export from the plan manager (SupportingU, MyIntegra, Plan Partners, etc.). We&apos;ll parse it with AI, let you review the lines, then apply the spend to this client&apos;s budget automatically.
            </p>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.csv,.xlsx,.xls,.txt,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => onFilePicked(e.target.files?.[0])}
              className="hidden"
              data-testid="stmt-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingStmt}
              className="btn-primary text-sm whitespace-nowrap"
              data-testid="stmt-upload-btn"
            >
              {uploadingStmt ? (<><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</>) : (<><Upload className="w-4 h-4" /> Upload statement</>)}
            </button>
          </div>
        </div>

        {statements.length > 0 && (
          <div className="mt-5 pt-5 border-t border-[#F0E5DE]" data-testid="statement-history">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-3">Statement history</div>
            <ul className="space-y-2">
              {visibleStatements.map((s) => (
                <li key={s.id} className="flex items-center justify-between bg-white border border-[#F0E5DE] rounded-2xl px-4 py-3 text-sm" data-testid={`statement-${s.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#1F2A2E] truncate">{s.plan_manager || "Plan manager statement"}</div>
                    <div className="text-xs text-[#6B7A80] mt-0.5">
                      {s.period_start ? `${fmtDate(s.period_start)} → ${fmtDate(s.period_end)}` : new Date(s.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })} · {s.entries_count} lines · ${fmt(s.total_amount)}
                    </div>
                  </div>
                  <button onClick={() => deleteStatement(s)} className="text-[#6B7A80] hover:text-red-500 ml-3" aria-label="Delete statement" data-testid={`delete-statement-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
            {statements.length > 3 && (
              <button onClick={() => setShowAllStatements((v) => !v)} className="text-xs font-semibold text-[#E2718A] hover:text-[#284952] mt-3">
                {showAllStatements ? "Show fewer" : `Show all ${statements.length}`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Empty state */}
      {totalItems === 0 ? (
        <div className="text-center py-14 bg-white rounded-3xl border border-[#F0E5DE]" data-testid="budget-empty">
          <Wallet className="w-12 h-12 mx-auto text-[#F4C3CC] mb-4" />
          <h3 className="font-display text-2xl text-[#1F2A2E] mb-2">No budget set up yet</h3>
          <p className="text-[#6B7A80] text-sm mb-6 max-w-md mx-auto">Seed all standard NDIS categories at $0 approved. Then edit each line with the approved amount from this participant&apos;s plan.</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <button onClick={seedCategories} disabled={seeding} className="btn-primary text-sm" data-testid="seed-budget-btn">
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Seed NDIS categories
            </button>
            <button onClick={() => setEditing("new")} className="btn-outline text-sm" data-testid="add-budget-item-btn">
              <Plus className="w-4 h-4" /> Add line manually
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Grand total */}
          <div className="bg-white rounded-3xl border border-[#F0E5DE] p-6" data-testid="grand-total">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80]">Plan Details</div>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "linear-gradient(90deg,#EA823A,#E2718A)" }} /> Spent</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#F4C3CC]" /> Allocated</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#F0E5DE]" /> Remaining</span>
                <button onClick={() => setEditing("new")} className="text-[#E2718A] font-semibold hover:text-[#284952] inline-flex items-center gap-1 ml-2" data-testid="add-budget-item-inline"><Plus className="w-3.5 h-3.5" /> Add line</button>
              </div>
            </div>
            <TotalsRow label="Total plan" totals={budget.grand_total} bold />
          </div>

          {/* Categories */}
          {["capacity", "core", "capital"].map((cat) => {
            const items = budget.grouped[cat] || [];
            if (items.length === 0) return null;
            const totals = budget.category_totals[cat];
            const style = CAT_STYLE[cat];
            return (
              <div key={cat} className="bg-white rounded-3xl border border-[#F0E5DE] overflow-hidden" data-testid={`budget-category-${cat}`}>
                <div className="px-6 py-4 flex items-center justify-between gap-2 flex-wrap" style={{ background: style.bg, borderBottom: `1px solid ${style.border}` }}>
                  <div className="font-display text-lg" style={{ color: style.text }}>{style.label}</div>
                  <div className="flex items-baseline gap-6 text-sm">
                    <TotalStat label="Approved" value={totals.approved} />
                    <TotalStat label="Spent"    value={totals.spent} />
                    <TotalStat label="Allocated" value={totals.allocated} />
                    <TotalStat label="Remaining" value={totals.remaining} accent />
                  </div>
                </div>
                <ul className="divide-y divide-[#F0E5DE]">
                  {items.map((it, i) => (
                    <LineItemRow
                      key={it.id || `orphan-${it.sub_category}-${i}`}
                      item={it}
                      onEdit={() => setEditing(it)}
                      onDelete={() => it.id && deleteItem(it.id)}
                      canDelete={!!it.id && isAdmin}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </>
      )}

      {editing && (
        <BudgetItemModal
          clientId={clientId}
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {preview && (
        <StatementPreviewModal
          preview={preview}
          onCancel={() => setPreview(null)}
          onCommit={commitStatement}
        />
      )}
    </div>
  );
}

function PeriodStat({ label, value, icon: Icon, hint, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-[#F0E5DE] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-[#F1E4D5] flex items-center justify-center text-[#E2718A]"><Icon className="w-4 h-4" /></div>
        <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80]">{label}</div>
      </div>
      <div className="font-display text-3xl text-[#1F2A2E] tabular-nums">{value}</div>
      {hint && <div className="text-xs text-[#6B7A80] mt-1">{hint}</div>}
    </div>
  );
}

function TotalStat({ label, value, accent }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7A80]">{label}</div>
      <div className={`font-semibold tabular-nums ${accent ? "text-[#E2718A]" : "text-[#1F2A2E]"}`}>${fmt(value)}</div>
    </div>
  );
}

function TotalsRow({ label, totals, bold }) {
  const cls = bold ? "font-display text-xl text-[#1F2A2E]" : "text-sm text-[#284952]";
  return (
    <div className="grid grid-cols-4 gap-6 items-end">
      <div className={cls}>{label}</div>
      <MetricCol label="Approved" value={totals.approved} />
      <MetricCol label="Spent" value={totals.spent} />
      <MetricCol label="Remaining" value={totals.remaining} accent />
    </div>
  );
}

function MetricCol({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7A80]">{label}</div>
      <div className={`font-display text-2xl tabular-nums ${accent ? "text-[#E2718A]" : "text-[#1F2A2E]"}`}>${fmt(value)}</div>
    </div>
  );
}

function LineItemRow({ item, onEdit, onDelete, canDelete }) {
  const pct = Math.min(100, Math.round(item.percent_used || 0));
  return (
    <li className="px-6 py-5 hover:bg-[#F1E4D5]/40 transition group" data-testid={`budget-line-${(item.id || "orphan")}`}>
      <div className="flex items-center gap-5 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <div className="font-medium text-[#1F2A2E]">{item.sub_category}</div>
            {item.orphan && (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#FCEADB] text-[#A0521A]" title="Auto-created from imported spend">
                needs approved $
              </span>
            )}
          </div>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <Cell label="Approved" value={`$${fmt(item.approved_amount)}`} />
            <Cell label="Spent" value={`$${fmt(item.spent)}`} sub={item.spent_from_statements > 0 || item.spent_from_tasks > 0 ? `${item.spent_from_tasks > 0 ? `Navica $${fmt(item.spent_from_tasks)}` : ""}${item.spent_from_tasks > 0 && item.spent_from_statements > 0 ? " · " : ""}${item.spent_from_statements > 0 ? `Statements $${fmt(item.spent_from_statements)}` : ""}` : null} />
            <Cell label="Allocated" value={`$${fmt(item.allocated)}`} />
            <Cell label="Remaining" value={`$${fmt(item.remaining)}`} accent={item.remaining < 0} />
          </div>
          {item.notes && <div className="text-xs text-[#6B7A80] mt-2 italic">{item.notes}</div>}
        </div>

        {/* Donut */}
        <Donut percent={pct} tone={item.remaining < 0 ? "danger" : (pct > 80 ? "warn" : "ok")} />

        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
          <button onClick={onEdit} className="w-8 h-8 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center text-[#284952] hover:text-[#E2718A]" aria-label="Edit" data-testid={`edit-budget-line-${item.id || "orphan"}`}>
            <Edit2 className="w-4 h-4" />
          </button>
          {canDelete && (
            <button onClick={onDelete} className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center text-[#6B7A80] hover:text-red-500" aria-label="Delete" data-testid={`delete-budget-line-${item.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function Cell({ label, value, sub, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80]">{label}</div>
      <div className={`font-semibold tabular-nums ${accent ? "text-red-600" : "text-[#1F2A2E]"}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#6B7A80] mt-0.5 truncate" title={sub}>{sub}</div>}
    </div>
  );
}

function Donut({ percent = 0, tone = "ok" }) {
  const size = 68, stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (percent / 100) * c;
  const colour = tone === "danger" ? "#DC2626" : tone === "warn" ? "#EA823A" : "#E2718A";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#F0E5DE" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={colour} strokeWidth={stroke} fill="none" strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-[#1F2A2E] tabular-nums">{percent}%</div>
    </div>
  );
}

// ── Budget item add / edit modal ────────────────────────────────
function BudgetItemModal({ clientId, item, onClose, onSaved }) {
  const isEdit = !!item?.id;
  const [form, setForm] = useState({
    category: item?.category || "capacity",
    sub_category: item?.sub_category || "Support Coordination",
    approved_amount: item?.approved_amount ?? "",
    notes: item?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, approved_amount: Number(form.approved_amount || 0) };
      if (isEdit) {
        await axios.put(`${API}/clients/${clientId}/budget/items/${item.id}`, payload);
      } else {
        await axios.post(`${API}/clients/${clientId}/budget/items`, payload);
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const options = STANDARD_SUBS[form.category] || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="budget-item-modal">
      <div className="bg-white rounded-[2rem] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#F0E5DE]">
          <h3 className="font-display text-2xl text-[#1F2A2E]">{isEdit ? "Edit line item" : "Add line item"}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close" data-testid="budget-modal-close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Category</label>
            <select
              value={form.category}
              onChange={(e) => {
                const cat = e.target.value;
                setForm((f) => ({ ...f, category: cat, sub_category: STANDARD_SUBS[cat][0] }));
              }}
              className="input-navica" data-testid="bi-category"
            >
              <option value="capacity">Capacity Building</option>
              <option value="core">Core Supports</option>
              <option value="capital">Capital Supports</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Sub-category</label>
            <input
              list="budget-subs"
              className="input-navica"
              value={form.sub_category}
              onChange={(e) => setForm({ ...form, sub_category: e.target.value })}
              required
              data-testid="bi-sub"
            />
            <datalist id="budget-subs">
              {options.map((o) => <option key={o} value={o} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Approved amount ($)</label>
            <input
              type="number" step="0.01" min="0"
              className="input-navica" value={form.approved_amount}
              onChange={(e) => setForm({ ...form, approved_amount: e.target.value })}
              placeholder="0.00" data-testid="bi-approved"
            />
            <p className="text-xs text-[#6B7A80] mt-1.5">The amount from the participant&apos;s NDIS plan document.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Notes (optional)</label>
            <textarea rows={2} className="input-navica resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="bi-notes" />
          </div>
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="bi-submit">
              {saving ? "Saving…" : "Save"} <Save className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Statement preview + commit ───────────────────────────────────
function StatementPreviewModal({ preview, onCancel, onCommit }) {
  const [meta, setMeta] = useState({
    period_start: preview.period_start || "",
    period_end: preview.period_end || "",
    plan_manager: preview.plan_manager || "",
    total_amount: preview.total_amount || 0,
    total_invoices: preview.total_invoices || preview.entries?.length || 0,
    filename: (preview._upload || {}).filename || "",
  });
  const [entries, setEntries] = useState(
    (preview.entries || []).map((e, i) => ({ ...e, _keep: true, _uid: i })),
  );
  const [saving, setSaving] = useState(false);

  const toggle = (uid) => setEntries((prev) => prev.map((e) => e._uid === uid ? { ...e, _keep: !e._keep } : e));
  const setField = (uid, key, val) => setEntries((prev) => prev.map((e) => e._uid === uid ? { ...e, [key]: val } : e));

  const keptCount = entries.filter((e) => e._keep).length;
  const keptTotal = entries.filter((e) => e._keep).reduce((s, e) => s + Number(e.amount || 0), 0);

  const commit = async () => {
    setSaving(true);
    try {
      const payload = {
        period_start: meta.period_start,
        period_end: meta.period_end,
        plan_manager: meta.plan_manager,
        total_amount: Number(meta.total_amount || 0),
        total_invoices: Number(meta.total_invoices || 0),
        filename: meta.filename,
        upload: preview._upload || null,
        entries: entries.filter((e) => e._keep).map((e) => ({
          activity_date: e.activity_date, provider: e.provider,
          ndis_support_item: e.ndis_support_item, sub_category: e.sub_category,
          amount: Number(e.amount || 0), status: e.status || "paid",
        })),
      };
      await onCommit(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onCancel} data-testid="statement-preview-modal">
      <div className="bg-white rounded-[2rem] w-full max-w-5xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-7 py-5 border-b border-[#F0E5DE] sticky top-0 bg-white z-10 rounded-t-[2rem]">
          <div>
            <h3 className="font-display text-2xl text-[#1F2A2E]">Review parsed statement</h3>
            <div className="text-xs text-[#6B7A80] mt-0.5">Tick the lines to import. Fix any wrong categories or amounts before saving.</div>
          </div>
          <button onClick={onCancel} className="w-10 h-10 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close" data-testid="stmt-preview-close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>

        {entries.length === 0 ? (
          <div className="p-10 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-[#EA823A] mb-3" />
            <div className="font-display text-xl text-[#1F2A2E] mb-2">No line items detected</div>
            <p className="text-sm text-[#6B7A80] max-w-md mx-auto">The AI couldn&apos;t find spend lines in this file. Try a clearer PDF export or a CSV with columns: Date, Provider, Item, Amount.</p>
            <button onClick={onCancel} className="btn-outline mt-6">Close</button>
          </div>
        ) : (
          <>
            {/* Meta fields */}
            <div className="p-7 grid md:grid-cols-4 gap-4 border-b border-[#F0E5DE]">
              <Meta label="Plan manager" value={meta.plan_manager} onChange={(v) => setMeta({ ...meta, plan_manager: v })} placeholder="e.g. SupportingU" testId="stmt-meta-pm" />
              <Meta label="Period start" type="date" value={meta.period_start} onChange={(v) => setMeta({ ...meta, period_start: v })} testId="stmt-meta-start" />
              <Meta label="Period end" type="date" value={meta.period_end} onChange={(v) => setMeta({ ...meta, period_end: v })} testId="stmt-meta-end" />
              <Meta label="Statement total ($)" type="number" value={meta.total_amount} onChange={(v) => setMeta({ ...meta, total_amount: v })} testId="stmt-meta-total" />
            </div>

            <div className="max-h-[55vh] overflow-y-auto">
              <table className="w-full text-sm" data-testid="stmt-entries-table">
                <thead className="bg-[#F1E4D5] sticky top-0 z-[1]">
                  <tr>
                    <Th></Th>
                    <Th>Date</Th>
                    <Th>Provider</Th>
                    <Th>Support item</Th>
                    <Th>Category</Th>
                    <Th>Amount</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0E5DE]">
                  {entries.map((e) => (
                    <tr key={e._uid} className={e._keep ? "hover:bg-[#F1E4D5]/50" : "opacity-40 line-through"} data-testid={`stmt-row-${e._uid}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={e._keep} onChange={() => toggle(e._uid)} className="navica-check" data-testid={`stmt-keep-${e._uid}`} />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <input type="date" className="w-32 px-2 py-1 text-xs rounded border border-[#F0E5DE]" value={e.activity_date} onChange={(ev) => setField(e._uid, "activity_date", ev.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        <input className="w-full px-2 py-1 text-xs rounded border border-[#F0E5DE]" value={e.provider} onChange={(ev) => setField(e._uid, "provider", ev.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        <input className="w-full px-2 py-1 text-xs rounded border border-[#F0E5DE]" value={e.ndis_support_item} onChange={(ev) => setField(e._uid, "ndis_support_item", ev.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        <select className="w-full px-2 py-1 text-xs rounded border border-[#F0E5DE]" value={e.sub_category} onChange={(ev) => setField(e._uid, "sub_category", ev.target.value)}>
                          {ALL_SUBS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <input type="number" step="0.01" className="w-24 px-2 py-1 text-xs rounded border border-[#F0E5DE] tabular-nums" value={e.amount} onChange={(ev) => setField(e._uid, "amount", ev.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        <select className="px-2 py-1 text-xs rounded border border-[#F0E5DE]" value={e.status || "paid"} onChange={(ev) => setField(e._uid, "status", ev.target.value)}>
                          <option value="paid">Paid</option>
                          <option value="allocated">Allocated</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-7 py-4 border-t border-[#F0E5DE] flex justify-between items-center gap-3 flex-wrap sticky bottom-0 bg-white rounded-b-[2rem]">
              <div className="text-sm text-[#6B7A80]">
                <span className="font-semibold text-[#1F2A2E]">{keptCount}</span> lines · <span className="font-semibold text-[#1F2A2E]">${fmt(keptTotal)}</span> total
              </div>
              <div className="flex gap-3">
                <button onClick={onCancel} className="btn-outline">Cancel</button>
                <button onClick={commit} disabled={saving || keptCount === 0} className="btn-primary" data-testid="stmt-commit-btn">
                  {saving ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : (<><CheckCircle2 className="w-4 h-4" /> Apply {keptCount} lines to budget</>)}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80]">{children}</th>;
}

function Meta({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80] mb-1">{label}</label>
      <input
        type={type}
        className="w-full px-3 py-2 text-sm rounded-xl border border-[#F0E5DE] focus:border-[#E2718A] outline-none"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
      />
    </div>
  );
}
